import { Attributes } from '@leverege/ui-attributes'
import { Flex, Text } from '@leverege/ui-elements'
import { useSharedContext } from '@leverege/ui-plugin'
import { AxisBottom, AxisLeft } from '@visx/axis'
import { localPoint } from '@visx/event'
import { Group } from '@visx/group'
import { LegendOrdinal } from '@visx/legend'
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale'
import { Bar, BarGroup, BarStack } from '@visx/shape'
import { TooltipWithBounds, defaultStyles, withTooltip } from '@visx/tooltip'
import React, { useMemo } from 'react'

import { getOpenHoursFromOperatingHours } from '../../../../util'
import Constants from '../../../../util/Constants'
import useStackedBarGroupData from './hooks/useStackedBarGroupData'

const { GRAPH_HEIGHT } = Constants

const BACKGROUND = '#fbfbfd'
const VERTICAL_MARGIN = 80
const HORIZONTAL_MARGIN = 50

const getDate = d => d.date
const getHour = d => d.hour

const formatTime = ( d ) => {
  return d
}

const tooltipStyles = {
  ...defaultStyles
}

function UsageStackedBarGroupChart( props ) {
  const {
    stations,
    width,
    data : pData,
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip,
    businessDays,
    showLegend = true
  } = props

  const { colorScale, legendColorScale, stationNames } = useMemo( () => {
    const stationNames = stations
      .sort( ( stationA, stationB ) => {
        let numberA = Attributes.get( 'station.number', stationA )
        let numberB = Attributes.get( 'station.number', stationB )

        numberA = numberA != null ? numberA : 0
        numberB = numberB != null ? numberB : 1

        return numberA > numberB ? 1 : -1
      } )
      .map( ( station, index ) => {
        const name = Attributes.get( 'name', station ) || station.data?.name
        if ( name != null ) {
          return name
        }

        return `Station ${index + 1}`
      } )
    const colorScale = scaleOrdinal( {
      domain : stationNames,
      range : [
        ...stationNames.map( ( item, index ) => `--color-stationBar${( index % 6 ) + 1}` )
      ],
    } )
    
    const legendColorScale = scaleOrdinal( {
      domain : stationNames,
      range : [
        ...stationNames.map( ( item, index ) => `var( --color-stationBar${( index % 6 ) + 1}Fill )` )
      ],
    } )

    return { colorScale, legendColorScale, stationNames }
  }, [ stations ] )

  const { operatingHours } = useSharedContext()

  const keys = useMemo( () => {
    return getOpenHoursFromOperatingHours( operatingHours, false )
  }, [ operatingHours ] )

  const data = useStackedBarGroupData( {
    businessDays,
    data : pData,
    openHours : keys,
    stationNames,
    operatingHours
  } )

  const hourScale = scaleBand( {
    domain : keys,
    padding : 0
  } )

  const dateScale = useMemo( () => {
    return scaleBand( {
      domain : data.map( getDate ),
      padding : 0.1
    } )
  }, [ data ] )

  const countScale = useMemo( () => {
    return scaleLinear( {
      round : true,
      domain : [ 0, Math.max( ...data.map( d => Math.max( ...keys.map( key => Number( d[key].count ) ) ) ), 1 ) ]
    } )
  }, [ data, keys ] )

  const xMax = width - HORIZONTAL_MARGIN
  const yMax = GRAPH_HEIGHT - VERTICAL_MARGIN

  dateScale.rangeRound( [ 0, xMax ] )
  hourScale.rangeRound( [ 0, dateScale.bandwidth() ] )
  countScale.range( [ yMax, 0 ] )

  return (
    <div style={{ position : 'relative' }}>
      <svg width={width} height={GRAPH_HEIGHT}>
        <rect x={0} y={0} width={width} height={GRAPH_HEIGHT} fill={BACKGROUND} rx={14} />
        <Group
          top={VERTICAL_MARGIN / 2}
          left={HORIZONTAL_MARGIN / 2}>
          <BarGroup
            data={data}
            keys={keys}
            height={yMax}
            x0={getDate}
            x0Scale={dateScale}
            x1Scale={hourScale}
            yScale={countScale}
            color={() => {}}>
            {barGroups => barGroups.map( barGroup => (
              <Group key={`bar-group-${barGroup.index}-${barGroup.x0}`} left={barGroup.x0} >
                <BarGroupStack
                  colorScale={colorScale}
                  stationNames={stationNames}
                  barGroup={barGroup}
                  xScale={hourScale}
                  yScale={countScale}
                  hideTooltip={hideTooltip}
                  showTooltip={showTooltip} />
              </Group>
            ) )}
          </BarGroup>
        </Group>
        <AxisLeft
          top={VERTICAL_MARGIN / 2}
          left={HORIZONTAL_MARGIN / 2}
          scale={countScale}
          hideTicks
          numTicks={6} />
        <AxisBottom
          top={yMax + VERTICAL_MARGIN / 2}
          left={HORIZONTAL_MARGIN / 2}
          tickFormat={formatTime}
          scale={dateScale}
          stroke="black"
          hideTicks
          tickLabelProps={() => ( {
            fill : 'black',
            fontSize : 11,
            textAnchor : 'middle',
          } )}/>
      </svg>
      <div
        style={{
          position : 'absolute',
          top : 0,
          width : '100%',
          display : 'flex',
          justifyContent : 'center',
          fontSize : '14px',
        }}>
        {showLegend && (
          <LegendOrdinal
            scale={legendColorScale}
            direction="row"
            labelMargin="0 15px 0 0" />
        )}
      </div>
      {tooltipData != null && tooltipOpen && (
        <TooltipWithBounds
          top={tooltipTop}
          left={tooltipLeft}
          style={{
            ...tooltipStyles,
            border : `1px solid var(${tooltipData.color}Stroke)`,
            boxShadow : `0 0px 8px var(${tooltipData.color}Fill)`
          }}>
          <Text variant="bodyBold">{tooltipData.bar.data.formattedHour}
          </Text>
          <hr
            style={{
              borderTop : `1px solid var(${tooltipData.color}Stroke)`
            }} />
          <Flex variant="rowM">
            <Text
              variant="bodyBold">{`${tooltipData.key}: `}
            </Text>
            <Text variant="body">{tooltipData.bar.data[tooltipData.key]}</Text>
          </Flex>
          <Flex variant="rowM">
            <Text variant="bodyBold">Total: </Text>
            <Text variant="body">{tooltipData.bar.data.count}</Text>
          </Flex>
        </TooltipWithBounds>
      )}
    </div>
  )
}

function BarGroupStack( {
  barGroup,
  xScale,
  yScale,
  hideTooltip,
  showTooltip,
  colorScale,
  stationNames
} ) {
  const data = useMemo( () => {
    return barGroup.bars.map( bar => bar.value )
  }, [ barGroup ] )

  return (
    <BarStack
      data={data}
      keys={stationNames}
      x={getHour}
      xScale={xScale}
      yScale={yScale}
      color={colorScale}>
      {barStacks => barStacks.map( barStack => barStack.bars.map( ( bar ) => {
        return (
          <Bar
            key={`bar-stack-${barStack.index}-${bar.index}`}
            x={bar.x}
            y={bar.y}
            height={bar.height}
            width={bar.width}
            fill={`var( ${bar.color}Fill )`}
            stroke={`var( ${bar.color}Stroke )`}
            onMouseLeave={() => {
              hideTooltip()
            }}
            onMouseMove={( event ) => {
              const eventSvgCoords = localPoint( event )

              showTooltip( {
                tooltipData : bar,
                tooltipTop : eventSvgCoords.y - 40,
                tooltipLeft : eventSvgCoords.x
              } )
            }} />
        )
      } ) )}
    </BarStack>
  )
}

export default withTooltip( UsageStackedBarGroupChart )
